import React, { useEffect, useState } from "react";
import { getGames } from "../../../../api/game";
import { getTexts, createText, updateText } from "../../../../api/text";
import { uploadSingleFile } from "../../../../api/files";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { SERVER_URL } from "../../../../api";
import { Button } from "@material-ui/core";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";

const VIDEO_TEXT_KEY = "video.intro.url";

function buildVideoUrl(value) {
	if (!value || typeof value !== "string") return null;
	if (value.startsWith("http")) return value;
	return `${SERVER_URL.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
}

function VideosPage() {
	const [games, setGames] = useState([]);
	const [selectedGameId, setSelectedGameId] = useState("");
	const [videoRecord, setVideoRecord] = useState(null);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
	const [removing, setRemoving] = useState(false);

	useEffect(() => {
		getGames()
			.then((res) => {
				if (res.status === 200 && res.data?.length) {
					setGames(res.data);
					const defaultGame = res.data.find((g) => g.default) || res.data[0];
					setSelectedGameId(defaultGame._id);
				}
			})
			.catch((error) => {
				alertError({ error, customMessage: "Could not get games." });
			});
	}, []);

	useEffect(() => {
		if (!selectedGameId) {
			setVideoRecord(null);
			return;
		}
		setLoading(true);
		getTexts({ game: selectedGameId })
			.then((res) => {
				if (res.status === 200 && Array.isArray(res.data)) {
					const rec = res.data.find((t) => t.key === VIDEO_TEXT_KEY);
					setVideoRecord(rec || null);
				} else {
					setVideoRecord(null);
				}
			})
			.catch(() => setVideoRecord(null))
			.finally(() => setLoading(false));
	}, [selectedGameId]);

	const videoUrl = videoRecord?.value ? buildVideoUrl(videoRecord.value) : null;

	const handleFileChange = async (e) => {
		const file = e.target?.files?.[0];
		const inputEl = document.getElementById("video-file-input");
		if (!file || !selectedGameId) return;
		setUploading(true);
		try {
			const uploadRes = await uploadSingleFile(file, "videos");
			if (uploadRes?.status !== 200 || !uploadRes.data?.fileURL) {
				throw new Error("Upload failed");
			}
			const filePath = uploadRes.data.fileURL;
			if (videoRecord?._id) {
				await updateText(videoRecord._id, { value: filePath });
			} else {
				await createText({
					game: selectedGameId,
					key: VIDEO_TEXT_KEY,
					order: 0,
					scenario: 0,
					value: filePath,
				});
			}
			const res = await getTexts({ game: selectedGameId });
			if (res.status === 200 && Array.isArray(res.data)) {
				const rec = res.data.find((t) => t.key === VIDEO_TEXT_KEY);
				setVideoRecord(rec || null);
			}
			alertSuccess({ customMessage: "Video guardado." });
		} catch (err) {
			alertError({ error: err, customMessage: "No se pudo subir el video." });
		} finally {
			setUploading(false);
			if (inputEl) inputEl.value = "";
		}
	};

	const handleRemoveVideo = async () => {
		if (!videoRecord?._id || !selectedGameId) return;
		setRemoving(true);
		try {
			await updateText(videoRecord._id, { value: "" });
			const res = await getTexts({ game: selectedGameId });
			if (res.status === 200 && Array.isArray(res.data)) {
				const rec = res.data.find((t) => t.key === VIDEO_TEXT_KEY);
				setVideoRecord(rec || null);
			} else {
				setVideoRecord(null);
			}
			setRemoveDialogOpen(false);
			alertSuccess({ customMessage: "Vídeo eliminado. La página /video mostrará vacío hasta que subas otro archivo." });
		} catch (err) {
			alertError({ error: err, customMessage: "No se pudo quitar el vídeo." });
		} finally {
			setRemoving(false);
		}
	};

	return (
		<>
			<Card>
				<CardHeader title="Video" />
				<CardBody>
					<div className="form-group mb-4">
						<label htmlFor="videos-game-select" className="font-weight-bold">
							Seleccionar juego
						</label>
						<select
							id="videos-game-select"
							className="form-control"
							style={{ maxWidth: 320 }}
							value={selectedGameId}
							onChange={(e) => setSelectedGameId(e.target.value)}
						>
							{games.map((game) => (
								<option key={game._id} value={game._id}>{game.fullName}</option>
							))}
						</select>
					</div>

					{loading ? (
						<p className="text-muted">Cargando…</p>
					) : (
						<>
							<div className="form-group">
								<label className="font-weight-bold d-block mb-2">Video de la página /video</label>
								<div className="mb-3">
									{videoUrl ? (
										<div className="rounded overflow-hidden bg-dark" style={{ maxWidth: 560, maxHeight: 320 }}>
											<video src={videoUrl} controls style={{ width: "100%", maxHeight: 320 }} />
										</div>
									) : (
										<div className="rounded bg-light p-4 text-muted" style={{ maxWidth: 560 }}>
											Sin video. Sube un archivo para la página /video.
										</div>
									)}
								</div>
								<input
									type="file"
									accept="video/*"
									className="d-none"
									id="video-file-input"
									onChange={handleFileChange}
									disabled={uploading}
								/>
								<div className="d-flex flex-wrap align-items-center">
									<label htmlFor="video-file-input" className="mb-0">
										<Button variant="outlined" color="primary" size="small" component="span" disabled={uploading}>
											{videoUrl ? "Cambiar video" : "Subir video"}
										</Button>
									</label>
									{videoUrl && videoRecord?._id && (
										<Button
											className="ml-2"
											variant="outlined"
											color="secondary"
											size="small"
											disabled={uploading || removing}
											onClick={() => setRemoveDialogOpen(true)}
										>
											{removing ? "Quitando…" : "Quitar vídeo"}
										</Button>
									)}
									{uploading && <span className="ml-2 small text-muted">Subiendo…</span>}
								</div>

							</div>
						</>
					)}
				</CardBody>
			</Card>
			<ConfirmDialog
				title="¿Quitar el vídeo de esta página?"
				open={removeDialogOpen}
				setOpen={setRemoveDialogOpen}
				onConfirm={handleRemoveVideo}
			>
				Se borrará la ruta del archivo guardada. Podrás subir otro vídeo cuando quieras.
			</ConfirmDialog>
		</>
	);
}

export default VideosPage;

